"use client";

import React, { useEffect } from "react";
import { Download, Info } from "lucide-react";
import { ButtonOutline } from "@/components/common/StandardButton";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { getVariantImageUrl } from "@/utils/helper";

interface FullInvoiceProps {
  orderId: string;
}

const FullInvoice: React.FC<FullInvoiceProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading, error } = useAppSelector(
    (state) => state.order,
  );

  // No need to fetch here - parent component handles it

  // Get first variant's assets from the order
  const getVariantAssets = () => {
    return selectedOrder?.variants?.[0]?.assets;
  };

  if (detailsLoading) {
    return (
      <div className="w-full border border-border rounded-lg overflow-hidden bg-card">
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
          <h3 className="font-medium text-foreground">Full Invoice</h3>
          <ButtonOutline size="sm" className="flex items-center gap-2" disabled>
            <Download className="h-4 w-4" />
            Download
          </ButtonOutline>
        </div>
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-muted rounded"></div>
                  <div>
                    <div className="h-4 w-24 bg-muted rounded mb-1"></div>
                    <div className="h-3 w-20 bg-muted rounded"></div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-4 w-8 bg-muted rounded"></div>
                  <div className="h-4 w-8 bg-muted rounded"></div>
                  <div className="h-4 w-12 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !selectedOrder) {
    return (
      <div className="w-full border border-border rounded-lg overflow-hidden bg-card">
        <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
          <h3 className="font-medium text-foreground">Full Invoice</h3>
          <ButtonOutline size="sm" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Download
          </ButtonOutline>
        </div>
        <div className="p-4 text-center text-muted-foreground">
          {error || "No invoice data available"}
        </div>
      </div>
    );
  }

  // Get all items from all order sets
  const allOrderSets = selectedOrder.order_sets || [];
  const invoiceItems = allOrderSets.flatMap((orderSet) => orderSet.items || []);
  const calculation = selectedOrder.calculation;
  const itemTotal = calculation?.items_sub_total || 0;
  const discountTotal = calculation?.total_discounts || 0;
  const taxTotal = calculation?.total_tax || 0;
  const chargesTotal = calculation?.total_charges || 0;
  const grandTotal = calculation?.grand_total_int || 0;

  return (
    <div className="w-full border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
        <h3 className="font-medium text-foreground">Full Invoice</h3>
        <ButtonOutline size="sm" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Download
        </ButtonOutline>
      </div>

      {/* Invoice Items */}
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Item List</span>
          <Info className="h-4 w-4 text-muted-foreground" />
        </div>

        <div className="space-y-3">
          {invoiceItems.map((item) => {
            const variantAssets = getVariantAssets();
            const imageUrl = getVariantImageUrl(variantAssets, 100, 100);

            return (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="relative h-8 w-8 rounded-md overflow-hidden bg-muted">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={item.title}
                        className="object-cover w-full h-full"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted-foreground/10 text-muted-foreground text-xs">
                        No Image
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.sku || "N/A"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.option1} {item.option2}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="text-center">
                    <p className="text-muted-foreground">Qty</p>
                    <p className="font-medium text-foreground">{item.quantity}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-muted-foreground">Tax</p>
                    <p className="font-medium text-foreground">
                      {item.taxable ? "18%" : "0%"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-muted-foreground">Price</p>
                    <p className="font-medium text-foreground">
                      ₹{(item.price / 100).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right min-w-16">
                    <p className="font-medium text-foreground">
                      ₹{((item.price * item.quantity) / 100).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-2 border-t border-border">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-foreground">Item Total:</span>
            <span className="font-medium text-foreground">
              ₹{(itemTotal / 100).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Discount List */}
        {discountTotal > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Discount List</span>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground">Order Discount</span>
                <span className="text-sm text-foreground">Applied</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-foreground">
                  Discount Amount:
                </span>
                <span className="text-sm font-medium text-foreground">
                  -₹{(discountTotal / 100).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="pt-2 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="font-medium text-foreground">
                  Discount Total:
                </span>
                <span className="font-medium text-foreground">
                  -₹{(discountTotal / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Tax Section */}
        {taxTotal > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Tax</span>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-foreground">Total Tax</span>
                <span className="text-foreground">
                  ₹{(taxTotal / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Charges */}
        {chargesTotal > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Charges</span>
              <Info className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-foreground">Total Charges</span>
                <span className="text-foreground">
                  ₹{(chargesTotal / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Grand Total */}
        <div className="pt-4 border-t-2 border-border">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-foreground">
              GRAND TOTAL
            </span>
            <span className="text-lg font-bold text-foreground">
              ₹{(grandTotal / 100).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullInvoice;
